/**
 * Local-only favourites store. Persists `schema.table` keys to localStorage.
 * Backend doesn't know or care; this is a per-browser convenience.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "kse-favorites-v1";

function read(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

export function useFavorites() {
  const [favs, setFavs] = useState<Set<string>>(read);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...favs]));
  }, [favs]);

  const isFav = useCallback((key: string) => favs.has(key), [favs]);
  const toggle = useCallback(
    (key: string) =>
      setFavs((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  return { favs, isFav, toggle };
}
